import { inject, Service } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { first, map, mergeMap, tap } from 'rxjs/operators';
import { Api, ApiClient } from '@app/api';
import { AccountService } from '@core/providers/account/account.service';
import { UsersBackendService } from '@core/providers/backend/users-backend.service';
import Bind from '@core/utils/decorators/bind.decorator';
import { VenueFromBackend } from '@shared/form-models-interfaces';
import { fetchAllPages } from '@shared/utils/fetch-all-pages';
import { ICampaignQr } from './campaign-qr.interface';
import { IGroupQr } from './group-qr.interface';
import { IVenueQr } from './venue-qr.interface';

@Service()
export class AccFolderQUrlsService {
  private accountService = inject(AccountService);
  private usersBackendService = inject(UsersBackendService);
  private api = inject(ApiClient);

  private groups$: BehaviorSubject<IGroupQr[]> = new BehaviorSubject<IGroupQr[]>([]);

  @Bind
  setGroups(data: IGroupQr[]) {
    this.groups$.next(data);
  }

  getGroups$(): Observable<IGroupQr[]> {
    return this.groups$;
  }

  getVenuesQrUrls$(): Observable<IVenueQr[]> {
    return this.accountService.getAccountContext$().pipe(
      first(),
      mergeMap(account =>
        fetchAllPages(page =>
          this.api.get(Api.fbi.accounts.venuesFixedUrls.paged, {
            pathParams: { uuid: account.uuid },
            query: { page, pageSize: 1000 },
          })
        )
      )
    );
  }

  /**
   * The standalone campaign URLs for the account, one per campaign and unit it is live at. The
   * endpoint names neither, so the unit list is joined in for a row that reads on its own.
   *
   * Admin-only on the backend, like the group list and unlike the unit list beside it.
   */
  getCampaignQrUrls$(): Observable<ICampaignQr[]> {
    return this.accountService.getAccountContext$().pipe(
      first(),
      mergeMap(account =>
        combineLatest([
          this.api.get(Api.surveys.campaign.accounts.qrUrls, {
            pathParams: { accountUuid: account.uuid },
          }),
          this.usersBackendService.getVenuesList$(account.uuid),
        ])
      ),
      map(([codes, venues]) => {
        const venueNames = new Map(
          venues.map((venue: VenueFromBackend) => [venue.uuid, venue.brandName])
        );

        return codes.map(code => ({
          campaignUuid: code.campaignUuid,
          campaignName: code.campaignName,
          venueUuid: code.venueUuid,
          venueName: venueNames.get(code.venueUuid) ?? '',
          url: code.url,
        }));
      })
    );
  }

  getGroupsQrUrls$(): Observable<IGroupQr[]> {
    return this.accountService.getAccountContext$().pipe(
      first(),
      mergeMap(account =>
        fetchAllPages(page =>
          this.api.get(Api.fbi.accounts.venueGroupsActiveFixedUrls.paged, {
            pathParams: { uuid: account.uuid },
            query: { page, pageSize: 1000 },
          })
        )
      ),
      tap(groups => this.setGroups(groups))
    );
  }

  /** Names a category's whole live set, so groups already live are left alone. */
  setCategoryActiveChildren$(categoryUuid: string, activeChildren: string[]): Observable<unknown> {
    return this.api.patch(Api.fbi.venuegroups.childrenActivity, {
      pathParams: { uuid: categoryUuid },
      body: { activeChildren },
    });
  }

  /**
   * Flips each group named, so only the ones whose state should change may be sent. It is the only
   * path for groups outside a category, which have no set-style endpoint.
   */
  toggleGroupsFixedUrl$(accountUuid: string, venueGroups: string[]): Observable<unknown> {
    return this.api.patch(Api.fbi.venuegroups.groupsActivity, {
      pathParams: { uuid: accountUuid },
      body: { venueGroups },
    });
  }

  setGroupFixedUrlActive$(venueGroupUuid: string, isActive: boolean): Observable<unknown> {
    return this.api.patch(Api.fbi.venuegroups.byUuid, {
      pathParams: { uuid: venueGroupUuid },
      body: { isActive },
    });
  }

  setAccountFixedUrlActive$(isActive: boolean): Observable<unknown> {
    return this.accountService.getAccountContext$().pipe(
      first(),
      mergeMap(account =>
        this.api.patch(Api.fbi.accounts.fixedUrl, {
          pathParams: { uuid: account.uuid },
          body: { isActive },
        })
      )
    );
  }
}
